import { useEffect, useMemo, useState } from "react";
import {
  BarChart3,
  FileText,
  IndianRupee,
  CalendarRange,
  Users,
  TrendingUp,
  X,
} from "lucide-react";
import { api } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const inr = (n) =>
  "₹ " +
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const compactInr = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return "₹ " + (v / 1e7).toFixed(2) + " Cr";
  if (v >= 1e5) return "₹ " + (v / 1e5).toFixed(2) + " L";
  if (v >= 1e3) return "₹ " + (v / 1e3).toFixed(1) + " K";
  return "₹ " + v.toFixed(0);
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDays = (d, days) => {
  const dt = new Date(d);
  dt.setDate(dt.getDate() + days);
  return dt.toISOString().slice(0, 10);
};
const firstOfMonthISO = (offset = 0) => {
  const d = new Date();
  d.setMonth(d.getMonth() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};
const lastOfMonthISO = (offset = 0) => {
  const d = new Date();
  d.setMonth(d.getMonth() + offset + 1);
  d.setDate(0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const monthLabel = (ym) => {
  const [y, m] = (ym || "").split("-");
  if (!y || !m) return ym;
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
};

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [buyer, setBuyer] = useState("__all__");

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (buyer && buyer !== "__all__") params.set("buyer", buyer);
      const qs = params.toString();
      const { data } = await api.get(`/analytics${qs ? "?" + qs : ""}`);
      setData(data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, buyer]);

  const quick = {
    lifetime: () => {
      setDateFrom("");
      setDateTo("");
    },
    thisMonth: () => {
      setDateFrom(firstOfMonthISO(0));
      setDateTo(todayISO());
    },
    lastMonth: () => {
      setDateFrom(firstOfMonthISO(-1));
      setDateTo(lastOfMonthISO(-1));
    },
    last90: () => {
      setDateFrom(addDays(todayISO(), -90));
      setDateTo(todayISO());
    },
    thisYear: () => {
      const y = new Date().getFullYear();
      setDateFrom(`${y}-01-01`);
      setDateTo(todayISO());
    },
  };

  const summary = data?.summary || {
    total_invoices: 0,
    total_amount: 0,
    month_invoices: 0,
    month_amount: 0,
    current_month: "",
  };

  const byMonth = useMemo(() => data?.by_month || [], [data]);
  const buyers = useMemo(() => data?.buyers || [], [data]);
  const maxMonthAmount = useMemo(
    () => byMonth.reduce((m, r) => Math.max(m, Number(r.amount) || 0), 0),
    [byMonth],
  );

  const hasRange = !!(dateFrom || dateTo);
  const hasBuyer = buyer && buyer !== "__all__";

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">
          Analytics
        </div>
        <h1
          className="text-4xl font-bold tracking-tight"
          data-testid="analytics-heading"
        >
          Dashboard
        </h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-xl">
          Lifetime, this month, and date-wise invoice totals. Filter by buyer /
          platform to see per-customer revenue.
        </p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <KpiCard
          testid="kpi-total-invoices"
          icon={FileText}
          label={hasRange || hasBuyer ? "Filtered Invoices" : "Lifetime Invoices"}
          value={summary.total_invoices.toLocaleString("en-IN")}
          sub={hasRange || hasBuyer ? "within current filter" : "all time"}
          tone="indigo"
        />
        <KpiCard
          testid="kpi-total-amount"
          icon={IndianRupee}
          label={hasRange || hasBuyer ? "Filtered Amount" : "Lifetime Amount"}
          value={compactInr(summary.total_amount)}
          sub={inr(summary.total_amount)}
          tone="emerald"
        />
        <KpiCard
          testid="kpi-month-invoices"
          icon={CalendarRange}
          label={`${monthLabel(summary.current_month)} — Invoices`}
          value={summary.month_invoices.toLocaleString("en-IN")}
          sub={hasBuyer ? `for ${buyer}` : "this month"}
          tone="violet"
        />
        <KpiCard
          testid="kpi-month-amount"
          icon={TrendingUp}
          label={`${monthLabel(summary.current_month)} — Amount`}
          value={compactInr(summary.month_amount)}
          sub={inr(summary.month_amount)}
          tone="amber"
        />
      </div>

      {/* Filter bar */}
      <Card className="p-4 mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <CalendarRange size={14} />
            <span className="uppercase tracking-wider">Filters</span>
          </div>

          <div className="flex items-center gap-2">
            <Label htmlFor="an-from" className="text-xs text-zinc-500">
              From
            </Label>
            <Input
              id="an-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-8 w-40 text-sm"
              data-testid="analytics-date-from"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="an-to" className="text-xs text-zinc-500">
              To
            </Label>
            <Input
              id="an-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-8 w-40 text-sm"
              data-testid="analytics-date-to"
            />
          </div>

          <div className="flex items-center gap-1">
            <QuickChip onClick={quick.lifetime} active={!hasRange} testid="qp-lifetime">
              Lifetime
            </QuickChip>
            <QuickChip onClick={quick.thisMonth} testid="qp-this-month">
              This month
            </QuickChip>
            <QuickChip onClick={quick.lastMonth} testid="qp-last-month">
              Last month
            </QuickChip>
            <QuickChip onClick={quick.last90} testid="qp-last-90">
              Last 90d
            </QuickChip>
            <QuickChip onClick={quick.thisYear} testid="qp-this-year">
              This year
            </QuickChip>
          </div>

          <div className="flex items-center gap-2 ml-auto min-w-[260px]">
            <Users size={14} className="text-zinc-500" />
            <Select value={buyer} onValueChange={setBuyer}>
              <SelectTrigger className="h-8 w-full text-sm" data-testid="analytics-buyer-select">
                <SelectValue placeholder="All buyers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__" data-testid="buyer-opt-all">All buyers</SelectItem>
                {buyers.map((b) => (
                  <SelectItem key={b.name} value={b.name} data-testid={`buyer-opt-${b.name}`}>
                    {b.name} <span className="text-zinc-400 text-xs ml-1">· {b.count}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasBuyer && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-zinc-500"
                onClick={() => setBuyer("__all__")}
                data-testid="buyer-clear"
                title="Clear buyer filter"
              >
                <X size={14} />
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Monthly trend */}
      <div className="grid grid-cols-5 gap-6 mb-6">
        <Card className="p-5 col-span-3" data-testid="analytics-trend-card">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 size={16} className="text-blue-600" />
            <div className="text-sm font-semibold">Monthly Trend</div>
            <div className="text-xs text-zinc-400 ml-auto">
              {byMonth.length} month{byMonth.length === 1 ? "" : "s"}
            </div>
          </div>
          {byMonth.length === 0 ? (
            <div className="text-xs text-zinc-400 py-12 text-center">
              No invoices in the selected range.
            </div>
          ) : (
            <div className="space-y-2">
              {byMonth
                .slice()
                .reverse()
                .map((row) => {
                  const pct = maxMonthAmount
                    ? Math.max(2, (Number(row.amount) / maxMonthAmount) * 100)
                    : 0;
                  return (
                    <div
                      key={row.month}
                      className="grid grid-cols-12 gap-3 items-center"
                      data-testid={`trend-row-${row.month}`}
                    >
                      <div className="col-span-2 text-xs mono text-zinc-500">
                        {monthLabel(row.month)}
                      </div>
                      <div className="col-span-7 h-7 bg-zinc-100 rounded relative overflow-hidden">
                        <div
                          className="h-full bg-blue-500/80 rounded"
                          style={{ width: `${pct}%`, transition: "width 400ms ease" }}
                        />
                        <div className="absolute inset-0 flex items-center px-2 text-[11px] font-medium text-white mix-blend-difference">
                          {compactInr(row.amount)}
                        </div>
                      </div>
                      <div className="col-span-3 text-right text-xs mono tabular text-zinc-600">
                        <b>{row.count}</b> inv · {inr(row.amount)}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </Card>

        <Card className="p-5 col-span-2" data-testid="analytics-top-buyers">
          <div className="flex items-center gap-2 mb-4">
            <Users size={16} className="text-violet-600" />
            <div className="text-sm font-semibold">Top Buyers</div>
            <div className="text-xs text-zinc-400 ml-auto">lifetime</div>
          </div>
          {buyers.length === 0 ? (
            <div className="text-xs text-zinc-400 py-12 text-center">No buyers yet.</div>
          ) : (
            <div className="space-y-1">
              {buyers.slice(0, 8).map((b, i) => (
                <button
                  type="button"
                  key={b.name}
                  onClick={() => setBuyer(b.name)}
                  className={`w-full flex items-center justify-between px-2 py-1.5 rounded hover:bg-zinc-50 text-left ${
                    buyer === b.name ? "bg-violet-50 border border-violet-200" : ""
                  }`}
                  data-testid={`top-buyer-${i}`}
                >
                  <div className="min-w-0">
                    <div className="text-xs font-medium truncate">{b.name}</div>
                    <div className="text-[10px] text-zinc-500">{b.count} invoice{b.count === 1 ? "" : "s"}</div>
                  </div>
                  <div className="text-xs mono tabular text-zinc-700 shrink-0 ml-2">
                    {compactInr(b.amount)}
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Breakdown table */}
      <Card className="overflow-hidden" data-testid="analytics-table-card">
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100">
          <div className="text-sm font-semibold">Month-wise breakdown</div>
          <div className="text-xs text-zinc-500">
            {hasBuyer && <span className="mr-2">buyer: <b>{buyer}</b></span>}
            {hasRange && (
              <span>
                range: <b>{dateFrom || "start"}</b> → <b>{dateTo || "today"}</b>
              </span>
            )}
            {!hasRange && !hasBuyer && <span>showing lifetime data</span>}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-500 text-left">
            <tr>
              <th className="py-3 px-4 font-medium">Month</th>
              <th className="py-3 px-4 font-medium text-right">Invoices</th>
              <th className="py-3 px-4 font-medium text-right">Amount (₹)</th>
              <th className="py-3 px-4 font-medium text-right">Avg / Invoice</th>
            </tr>
          </thead>
          <tbody>
            {loading && byMonth.length === 0 ? (
              <tr>
                <td colSpan="4" className="py-10 text-center text-zinc-400 text-xs">
                  Loading…
                </td>
              </tr>
            ) : byMonth.length === 0 ? (
              <tr>
                <td colSpan="4" className="py-10 text-center text-zinc-400 text-xs">
                  No invoices in the selected range.
                </td>
              </tr>
            ) : (
              byMonth
                .slice()
                .reverse()
                .map((row) => (
                  <tr
                    key={row.month}
                    className="border-t border-zinc-100"
                    data-testid={`breakdown-row-${row.month}`}
                  >
                    <td className="py-3 px-4 mono">{monthLabel(row.month)}</td>
                    <td className="py-3 px-4 text-right mono tabular">{row.count}</td>
                    <td className="py-3 px-4 text-right mono tabular">
                      {Number(row.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </td>
                    <td className="py-3 px-4 text-right mono tabular text-zinc-500">
                      {Number(row.amount / Math.max(row.count, 1)).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                ))
            )}
          </tbody>
          {byMonth.length > 0 && (
            <tfoot className="bg-zinc-50 font-semibold">
              <tr>
                <td className="py-3 px-4">Total</td>
                <td className="py-3 px-4 text-right mono tabular" data-testid="breakdown-total-count">
                  {summary.total_invoices}
                </td>
                <td className="py-3 px-4 text-right mono tabular" data-testid="breakdown-total-amount">
                  {Number(summary.total_amount).toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                  })}
                </td>
                <td className="py-3 px-4 text-right mono tabular text-zinc-500">
                  {summary.total_invoices > 0
                    ? Number(summary.total_amount / summary.total_invoices).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                      })
                    : "0.00"}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </Card>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, tone = "indigo", testid }) {
  const toneClasses = {
    indigo: "bg-indigo-50 text-indigo-600",
    emerald: "bg-emerald-50 text-emerald-600",
    violet: "bg-violet-50 text-violet-600",
    amber: "bg-amber-50 text-amber-600",
  }[tone] || "bg-zinc-100 text-zinc-600";
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 truncate">
            {label}
          </div>
          <div className="text-2xl font-bold mt-1 mono tabular" data-testid={`${testid}-value`}>
            {value}
          </div>
          <div className="text-[11px] text-zinc-500 mt-1 truncate">{sub}</div>
        </div>
        <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${toneClasses}`}>
          <Icon size={18} />
        </div>
      </div>
    </Card>
  );
}

function QuickChip({ onClick, children, active, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-8 px-3 text-xs rounded-md border transition ${
        active
          ? "border-blue-500 bg-blue-50 text-blue-700"
          : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300"
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}
